
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Settings, List } from "lucide-react";

export interface ColumnDefinition {
  id: string;
  label: string;
  group?: string;
}

interface ColumnChooserProps {
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onVisibleColumnsChange: (columns: string[]) => void;
}

const ColumnChooser: React.FC<ColumnChooserProps> = ({
  columns,
  visibleColumns,
  onVisibleColumnsChange,
}) => {
  // Group columns by their group property
  const groupedColumns = columns.reduce((acc, column) => {
    const group = column.group || 'General';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(column);
    return acc;
  }, {} as Record<string, ColumnDefinition[]>);

  // Handle toggling a single column
  const toggleColumn = (columnId: string) => {
    onVisibleColumnsChange(
      visibleColumns.includes(columnId)
        ? visibleColumns.filter(id => id !== columnId)
        : [...visibleColumns, columnId]
    );
  };

  // Handle toggling all columns in a group
  const toggleGroup = (groupName: string, groupColumns: ColumnDefinition[]) => {
    const groupColumnIds = groupColumns.map(col => col.id);
    const allVisible = groupColumns.every(col => visibleColumns.includes(col.id));
    
    if (allVisible) {
      // Remove all columns in this group
      onVisibleColumnsChange(
        visibleColumns.filter(id => !groupColumnIds.includes(id))
      );
    } else {
      // Add all missing columns in this group
      const missingColumns = groupColumnIds.filter(id => !visibleColumns.includes(id));
      onVisibleColumnsChange([...visibleColumns, ...missingColumns]);
    }
  };

  return (
    <div className="hidden md:block">
      <Popover>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm"
            className="gap-1 items-center"
          >
            <List size={16} className="mr-1" />
            <span>Columns</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="end">
          <ScrollArea className="h-[400px] p-4">
            {Object.entries(groupedColumns).map(([groupName, groupColumns]) => (
              <div key={groupName} className="mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Checkbox 
                    id={`group-${groupName}`}
                    checked={groupColumns.every(col => visibleColumns.includes(col.id))}
                    onCheckedChange={() => toggleGroup(groupName, groupColumns)}
                  />
                  <label 
                    htmlFor={`group-${groupName}`} 
                    className="text-sm font-medium cursor-pointer hover:text-foreground"
                  >
                    {groupName}
                  </label>
                </div>
                <div className="space-y-2 pl-6">
                  {groupColumns.map(column => (
                    <div key={column.id} className="flex items-center gap-2">
                      <Checkbox 
                        id={`col-${column.id}`}
                        checked={visibleColumns.includes(column.id)}
                        onCheckedChange={() => toggleColumn(column.id)}
                      />
                      <label 
                        htmlFor={`col-${column.id}`} 
                        className="text-sm cursor-pointer hover:text-foreground"
                      >
                        {column.label}
                      </label>
                    </div>
                  ))}
                </div>
                <Separator className="mt-2" />
              </div>
            ))}
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ColumnChooser;
